import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, Phone, User, ChevronDown } from 'lucide-react';

const NAV_ITEMS = [
  { path: '/', label: 'Home' },
  { path: '/about', label: 'About' },
  {
    path: '/services',
    label: 'Services',
    dropdown: [
      { path: '/services', label: 'All Services' },
      { path: '/treatments/ayurveda', label: 'Ayurveda Treatment' },
      { path: '/treatments/surgery', label: 'Surgery' },
      { path: '/treatments/yoga', label: 'Yoga Therapy' },
      { path: '/treatments/consultation', label: 'Medical Consultations' },
    ]
  },
  { path: '/treatments', label: 'Treatments' },
  { path: '/blog', label: 'Blog' },
];

export const Header = ({ onContactClick }) => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState({});

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleMobileExpand = (path) => {
    setMobileExpanded(prev => ({ ...prev, [path]: !prev[path] }));
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${isScrolled ? 'shadow-md' : 'shadow-sm'
        }`}
    >
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo - balanced size and alignment */}
          <Link
            to="/"
            className="flex items-center shrink-0 focus:outline-none focus:ring-2 focus:ring-brand-saffron/30 rounded-lg"
            aria-label="BharatHeal Home"
          >
            <img loading="lazy" decoding="async"
              src="/logo.png"
              alt="BharatHeal Logo"
              className="h-10 sm:h-12 md:h-14 w-auto object-contain object-center"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {NAV_ITEMS.map((item) => {
              if (item.dropdown) {
                return (
                  <div key={item.path} className="relative group">
                    <button
                      className={`flex items-center gap-1 text-gray-700 hover:text-brand-navy transition-colors duration-200 font-medium relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-brand-navy after:transition-all after:duration-300 group-hover:after:w-full ${location.pathname.startsWith(item.path) || (item.path === '/services' && location.pathname.startsWith('/treatments/')) ? 'text-brand-navy font-semibold after:w-full' : ''
                        }`}
                    >
                      {item.label}
                      <ChevronDown className="h-4 w-4 transition-transform duration-300 group-hover:rotate-180" />
                    </button>
                    <div className="absolute left-0 mt-2 w-56 rounded-xl shadow-xl bg-white/95 backdrop-blur-md ring-1 ring-black/5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform origin-top-left z-50 animate-in fade-in slide-in-from-top-2 border border-gray-100">
                      <div className="py-2">
                        {item.dropdown.map((subItem) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            className={`block px-4 py-3 text-sm text-gray-700 hover:bg-brand-blue/5 hover:text-brand-blue hover:pl-6 transition-all duration-300 ${location.pathname === subItem.path ? 'bg-brand-blue/5 text-brand-blue font-medium pl-6' : ''
                              }`}
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-gray-700 hover:text-brand-navy transition-colors duration-200 font-medium relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-brand-navy after:transition-all after:duration-300 hover:after:w-full ${(item.path === '/' && location.pathname === '/') ||
                    (item.path !== '/' && location.pathname === item.path)
                    ? 'text-brand-navy font-semibold after:w-full' : ''
                    }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Contact & Patient Portal */}
          <div className="hidden lg:flex items-center gap-4">
            <Link to="/customer/login">
              <Button variant="outline" className="rounded-lg">
                <User className="mr-2 h-4 w-4" />
                Patient Portal
              </Button>
            </Link>
            <Link to="/contact">
              <Button className={`bg-brand-saffron hover:bg-brand-saffron/90 text-brand-navy font-semibold rounded-lg transition-all duration-300 ${location.pathname === '/contact' ? 'ring-2 ring-brand-saffron/50' : ''
                }`}>
                <Phone className="mr-2 h-4 w-4" />
                Contact Us
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-gray-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200 h-[calc(100vh-80px)] overflow-y-auto">
            <nav className="flex flex-col gap-4">
              {NAV_ITEMS.map((item) => {
                const isActive = (item.path === '/' && location.pathname === '/') ||
                  (item.path !== '/' && location.pathname === item.path) ||
                  (item.dropdown && (location.pathname.startsWith(item.path) || (item.path === '/services' && location.pathname.startsWith('/treatments/'))));

                if (item.dropdown) {
                  return (
                    <div key={item.path} className="flex flex-col">
                      <button
                        onClick={() => toggleMobileExpand(item.path)}
                        className={`flex items-center justify-between py-2 text-gray-700 hover:text-brand-navy transition-colors duration-200 font-medium text-left ${isActive ? 'text-brand-navy font-semibold' : ''}`}
                      >
                        {item.label}
                        <ChevronDown className={`h-4 w-4 transition-transform ${mobileExpanded[item.path] ? 'rotate-180' : ''}`} />
                      </button>
                      {mobileExpanded[item.path] && (
                        <div className="pl-4 flex flex-col gap-3 mt-2 border-l-2 border-brand-saffron/20 ml-2">
                          {item.dropdown.map((subItem) => (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              onClick={handleNavClick}
                              className={`text-gray-600 hover:text-brand-navy text-sm ${location.pathname === subItem.path ? 'text-brand-navy font-medium' : ''
                                }`}
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={handleNavClick}
                    className={`text-gray-700 hover:text-brand-navy transition-colors duration-200 font-medium text-left ${isActive ? 'text-brand-navy font-semibold' : ''}`}
                  >
                    {item.label}
                  </Link>
                );
              })}
              <Link to="/customer/login" className="w-full mt-2" onClick={handleNavClick}>
                <Button variant="outline" className="w-full">
                  <User className="mr-2 h-4 w-4" />
                  Patient Portal
                </Button>
              </Link>
              <Link to="/contact" onClick={handleNavClick}>
                <Button className={`bg-brand-saffron hover:bg-brand-saffron/90 text-brand-navy font-semibold w-full ${location.pathname === '/contact' ? 'ring-2 ring-brand-saffron/50' : ''
                  }`}>
                  <Phone className="mr-2 h-4 w-4" />
                  Contact Us
                </Button>
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
